import IMessageData, {
  ISystemMessageData,
  CombinedMessage,
} from '@th-types/message.type';
import { Pagination } from '@th-types/pageable.type';
import { IChatMember, ChatMemberType } from '@th-types/person.type';

export interface ChatMemberDisplay {
  id: number;
  fullName: string;
  profileImageUrl: string;
  type?: ChatMemberType;
}

export enum ChatType {
  JOB = 'job',
  DIRECT = 'direct',
}

export enum ChatEntityType {
  WORKER = 'worker',
  COMPANY = 'company',
}

export interface ChatEntity {
  id: number;
  type: ChatEntityType;
  name: string;
  profileImageUrl?: string;
  email?: string;
  cellPhone?: string;
}

export interface ChatJobInfo {
  jobId?: number;
  jobName?: string;
  eventName?: string;
  address?: string;
  jobPosition?: string;
  startDate?: string;
  endDate?: string;
  jobDetailUrl?: string;
}

export interface ChatStatusBadge {
  label: string;
  color?: string;
}

export interface ChatTarget {
  chatType?: ChatType;
  jobAssignmentId: number;
  entity: ChatEntity;
  jobInfo?: ChatJobInfo;
  statusBadges?: ChatStatusBadge[];
  showContactInfo?: boolean;
  unreadMessages?: number;
  firstUnreadMessageId?: number | null;
  isAnnouncement?: boolean;
}

export type ChatMode = 'drawer' | 'embedded';

export const SENDABLE_ASSIGNMENT_STATUSES = [
  'SHARED',
  'APPLIED',
  'OFFERED',
  'HIRED',
  'WORKING',
  'COMPLETED',
] as const;

export interface ChatState {
  isOpen: boolean;
  mode: ChatMode;
  activeChat: ChatTarget | null;
  currentUserId: number | null;
  initialFirstUnreadMessageId: number | null;
  messages: IMessageData[];
  systemMessages: ISystemMessageData[];
  chatMembers: ChatMemberDisplay[];
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
  isSendingMsgAllowed: boolean;
  pagination: Pagination | null;
  hasMoreOlder: boolean;
  isLoadingOlder: boolean;
}

export interface ChatActions {
  openChat: (target: ChatTarget, currentUserId?: number) => Promise<void>;
  closeChat: () => void;
  loadChat: (target: ChatTarget, currentUserId?: number) => Promise<void>;
  loadOlderMessages: () => Promise<void>;
  sendMessage: (text: string) => Promise<boolean>;
  removeMessage: (messageId: number) => void;
  clearError: () => void;
  reset: () => void;
}

export type ChatStore = ChatState & ChatActions;

export interface ChatMessagesResponse {
  messages: IMessageData[];
  systemMessages: ISystemMessageData[];
  chatMembers?: IChatMember[];
  jobChatId: number;
  jobAssignment?: {
    id: number;
    jobId: number;
    status: string;
  };
}

export type {
  IMessageData,
  ISystemMessageData,
  CombinedMessage,
  IChatMember,
  ChatMemberType,
};
